import React, { useState } from 'react';
import { useConfig } from '../../config/ConfigContext';
import { isCommunityFeatureEnabled } from '../../utils/featureHelpers';
import RatingInput from './RatingInput';
import FeedbackSection, { CommentTextarea } from './FeedbackSection';
import '../../styles/feedback.css';

/**
 * Main feedback form component with feature-flag-aware sections
 * @param {Object} props - Component props
 * @param {Function} props.onSubmit - Callback when form is submitted
 * @param {boolean} [props.loading=false] - Whether form is submitting
 * @returns {JSX.Element} FeedbackForm component
 */
const FeedbackForm = ({ onSubmit, loading = false }) => {
  const { config, loading: configLoading } = useConfig();

  // Form state
  const [formData, setFormData] = useState({
    // User info (optional)
    user_name: '',
    user_email: '',

    // Section being submitted (for targeted feedback)
    section: 'general',

    // Ratings
    ratings: {
      overall_satisfaction: 0,
      // Lectures
      lectures_transcription: 0,
      lectures_summary: 0,
      lectures_notes: 0,
      lectures_takeaways: 0,
      // Video
      video_playback: 0,
      shorts_experience: 0,
      // Announcements
      announcements_usefulness: 0,
      announcements_clarity: 0,
      // Calendar
      calendar_usability: 0,
      calendar_completeness: 0,
      // Publications
      publications_quality: 0,
      publications_accessibility: 0,
    },

    // Comments
    comments: {
      lectures: '',
      video: '',
      announcements: '',
      calendar: '',
      publications: '',
    },

    // General feedback
    improvement_suggestions: '',
    additional_comments: '',
  });

  const [errors, setErrors] = useState({});

  // Feature flags
  const hasAnnouncements = isCommunityFeatureEnabled(config, 'announcements');
  const hasCalendar = isCommunityFeatureEnabled(config, 'calendar');

  const updateRating = (key, value) => {
    setFormData(prev => ({
      ...prev,
      ratings: { ...prev.ratings, [key]: value }
    }));
    // Clear error when rating is provided
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: null }));
    }
  };

  const updateComment = (section, value) => {
    setFormData(prev => ({
      ...prev,
      comments: { ...prev.comments, [section]: value }
    }));
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    const newErrors = {};

    // At least one rating or comment required
    const hasAnyRating = Object.values(formData.ratings).some(v => v > 0);
    const hasAnyComment = Object.values(formData.comments).some(v => v.trim().length > 0);
    const hasGeneralFeedback = formData.improvement_suggestions.trim().length > 0 ||
                               formData.additional_comments.trim().length > 0;

    if (!hasAnyRating && !hasAnyComment && !hasGeneralFeedback) {
      newErrors.general = 'Please provide at least one rating or comment';
    }

    // Validate email format if provided
    if (formData.user_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.user_email)) {
      newErrors.user_email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Prepare submission data with center info from config
    const submissionData = {
      center_id: config?.center_id,
      center_name: config?.name,
      center_code: config?.center_code,
      ...formData
    };

    if (onSubmit) {
      onSubmit(submissionData);
    }
  };

  if (configLoading) {
    return (
      <div className="feedback-form-loading">
        <div className="loading-spinner" />
        <p>Loading feedback form...</p>
      </div>
    );
  }

  return (
    <form className="feedback-form" onSubmit={handleSubmit} noValidate>
      {/* Center Info (read-only display) */}
      <div className="feedback-center-info">
        <span className="feedback-center-badge">
          Providing feedback for: <strong>{config?.name || 'Center'}</strong>
        </span>
      </div>

      {/* Overall Satisfaction */}
      <div className="feedback-overall-section">
        <RatingInput
          label="Overall Satisfaction"
          value={formData.ratings.overall_satisfaction}
          onChange={(v) => updateRating('overall_satisfaction', v)}
          name="overall_satisfaction"
        />
      </div>

      {/* Lectures Section - Always visible */}
      <FeedbackSection
        title="Lectures & Products"
        icon="lectures"
        visible={true}
        defaultExpanded={true}
      >
        <div className="feedback-ratings-grid">
          <RatingInput
            label="Transcription Quality"
            value={formData.ratings.lectures_transcription}
            onChange={(v) => updateRating('lectures_transcription', v)}
          />
          <RatingInput
            label="Summary Quality"
            value={formData.ratings.lectures_summary}
            onChange={(v) => updateRating('lectures_summary', v)}
          />
          <RatingInput
            label="Notes Quality"
            value={formData.ratings.lectures_notes}
            onChange={(v) => updateRating('lectures_notes', v)}
          />
          <RatingInput
            label="Key Takeaways"
            value={formData.ratings.lectures_takeaways}
            onChange={(v) => updateRating('lectures_takeaways', v)}
          />
        </div>
        <CommentTextarea
          label="Comments on Lectures"
          value={formData.comments.lectures}
          onChange={(v) => updateComment('lectures', v)}
          placeholder="Share your thoughts on lecture content, transcription accuracy, or any issues..."
        />
      </FeedbackSection>

      {/* Video Section - Always visible */}
      <FeedbackSection
        title="Video Experience"
        icon="video"
        visible={true}
        defaultExpanded={false}
      >
        <div className="feedback-ratings-grid">
          <RatingInput
            label="Video Playback"
            value={formData.ratings.video_playback}
            onChange={(v) => updateRating('video_playback', v)}
          />
          <RatingInput
            label="Shorts Experience"
            value={formData.ratings.shorts_experience}
            onChange={(v) => updateRating('shorts_experience', v)}
          />
        </div>
        <CommentTextarea
          label="Comments on Video"
          value={formData.comments.video}
          onChange={(v) => updateComment('video', v)}
          placeholder="Any issues with video loading, playback, or the Shorts feature..."
        />
      </FeedbackSection>

      {/* Announcements Section - Feature flag controlled */}
      <FeedbackSection
        title="Announcements"
        icon="announcements"
        visible={hasAnnouncements}
        defaultExpanded={false}
      >
        <div className="feedback-ratings-grid">
          <RatingInput
            label="Usefulness"
            value={formData.ratings.announcements_usefulness}
            onChange={(v) => updateRating('announcements_usefulness', v)}
          />
          <RatingInput
            label="Clarity"
            value={formData.ratings.announcements_clarity}
            onChange={(v) => updateRating('announcements_clarity', v)}
          />
        </div>
        <CommentTextarea
          label="Comments on Announcements"
          value={formData.comments.announcements}
          onChange={(v) => updateComment('announcements', v)}
          placeholder="Feedback on announcements content or delivery..."
        />
      </FeedbackSection>

      {/* Calendar Section - Feature flag controlled */}
      <FeedbackSection
        title="Calendar & Events"
        icon="calendar"
        visible={hasCalendar}
        defaultExpanded={false}
      >
        <div className="feedback-ratings-grid">
          <RatingInput
            label="Usability"
            value={formData.ratings.calendar_usability}
            onChange={(v) => updateRating('calendar_usability', v)}
          />
          <RatingInput
            label="Completeness"
            value={formData.ratings.calendar_completeness}
            onChange={(v) => updateRating('calendar_completeness', v)}
          />
        </div>
        <CommentTextarea
          label="Comments on Calendar"
          value={formData.comments.calendar}
          onChange={(v) => updateComment('calendar', v)}
          placeholder="Suggestions for the calendar or event listings..."
        />
      </FeedbackSection>

      {/* Publications Section - Always visible */}
      <FeedbackSection
        title="Publications"
        icon="publications"
        visible={true}
        defaultExpanded={false}
      >
        <div className="feedback-ratings-grid">
          <RatingInput
            label="Content Quality"
            value={formData.ratings.publications_quality}
            onChange={(v) => updateRating('publications_quality', v)}
          />
          <RatingInput
            label="Accessibility"
            value={formData.ratings.publications_accessibility}
            onChange={(v) => updateRating('publications_accessibility', v)}
          />
        </div>
        <CommentTextarea
          label="Comments on Publications"
          value={formData.comments.publications}
          onChange={(v) => updateComment('publications', v)}
          placeholder="Feedback on PDF quality, loading, or content..."
        />
      </FeedbackSection>

      {/* General Feedback Section - Always visible */}
      <FeedbackSection
        title="General Feedback"
        icon="general"
        visible={true}
        defaultExpanded={true}
      >
        <CommentTextarea
          label="Improvement Suggestions"
          value={formData.improvement_suggestions}
          onChange={(v) => updateField('improvement_suggestions', v)}
          placeholder="What features or improvements would you like to see?"
          maxLength={1000}
        />
        <CommentTextarea
          label="Additional Comments"
          value={formData.additional_comments}
          onChange={(v) => updateField('additional_comments', v)}
          placeholder="Any other feedback or thoughts you'd like to share..."
          maxLength={1000}
        />
      </FeedbackSection>

      {/* Optional User Info */}
      <div className="feedback-user-info-section">
        <h4>Optional: Your Information</h4>
        <p className="feedback-user-info-note">
          Share your contact info if you'd like us to follow up with you.
        </p>
        <div className="feedback-user-inputs">
          <div className="feedback-input-group">
            <label htmlFor="user_name">Name</label>
            <input
              type="text"
              id="user_name"
              value={formData.user_name}
              onChange={(e) => updateField('user_name', e.target.value)}
              placeholder="Your name (optional)"
              maxLength={100}
            />
          </div>
          <div className="feedback-input-group">
            <label htmlFor="user_email">Email</label>
            <input
              type="email"
              id="user_email"
              value={formData.user_email}
              onChange={(e) => updateField('user_email', e.target.value)}
              placeholder="your@email.com (optional)"
              maxLength={100}
              className={errors.user_email ? 'error' : ''}
            />
            {errors.user_email && (
              <span className="feedback-error-message">{errors.user_email}</span>
            )}
          </div>
        </div>
      </div>

      {/* Error Display */}
      {errors.general && (
        <div className="feedback-form-error" role="alert">
          {errors.general}
        </div>
      )}

      {/* Submit Button */}
      <div className="feedback-submit-section">
        <button
          type="submit"
          className="feedback-submit-button"
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="loading-spinner-small" />
              Submitting...
            </>
          ) : (
            'Submit Feedback'
          )}
        </button>
      </div>
    </form>
  );
};

export default FeedbackForm;
